import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bg-slateGray" }

import { ref, computed, reactive } from 'vue'
import type { Ref } from 'vue'
import useLanguage from '@/composables/useLanguage'
import { MJPSection, MJPSectionData } from './types'
import SectionBase from '../general/SectionBase.vue'
import SectionPage from '../general/SectionPage.vue'
import TextCard from '../general/TextCard.vue'
import BottomGradient from '../general/BottomGradient.vue'
import ResponsiveBackgroundImage from '../general/ResponsiveBackgroundImage.vue'
import ResponsiveBackground from '../general/ResponsiveBackground.vue'

interface Props {
  sectionData: MJPSectionData
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  forward: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ConclusionSection',
  props: {
    sectionData: null,
    currentSection: null,
    currentSectionHasLoaded: { type: Boolean },
    currentPage: null,
    forward: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  sectionData: MJPSectionData
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  forward: boolean
}


const sectionBase: Ref<typeof SectionBase | null> = ref(null)
const text = useLanguage(props.currentSection)
const state = reactive({
  prefersReducedMotion: matchMedia('(prefers-reduced-motion)').matches,
})

// the following methods are repeated in each section,
// currently preferring template readability over DRY
const narrativeScrollProgress = computed(() => {
  return sectionBase.value ? sectionBase.value.state.narrativeScrollProgress : 0
})

const responsiveScrollOffset = computed(() => {
  return sectionBase.value
    ? sectionBase.value.state.responsiveScrollOffset
    : {
        proxyPosition: '',
        offsetX: 0,
      }
})

const sectionIsActive = computed(() => {
  return props.currentSection == props.sectionData.section
})

const pageRangeIsActive = (lo, hi) => {
  if (sectionIsActive.value && props.currentSectionHasLoaded) {
    return props.currentPage >= lo && props.currentPage <= hi
  } else {
    return false
  }
}

const opacityForPageRange = (lo, hi) => {
  return pageRangeIsActive(lo, hi) ? 1 : 0
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ResponsiveBackgroundImage, {
      id: `${props.sectionData.section}-bg`,
      dir: "mjp",
      sm: "conclusion-bg-sm.jpg",
      md: "conclusion-bg-md.jpg",
      lg: "conclusion-bg-lg.jpg",
      alt: _unref(text).altText,
      isActive: pageRangeIsActive(0, 99)
    }, null, 8, ["id", "alt", "isActive"]),
    (!state.prefersReducedMotion)
      ? (_openBlock(), _createBlock(ResponsiveBackground, {
          key: 0,
          id: `${props.sectionData.section}-illo`,
          scrollOffset: _unref(responsiveScrollOffset),
          altText: _unref(text).illoAltText,
          position: "50% 50%",
          dir: "mjp",
          sm: "conclusion.gif",
          md: "conclusion.gif",
          lg: "conclusion.gif",
          style: _normalizeStyle({
        opacity: opacityForPageRange(2, 99),
      })
        }, null, 8, ["id", "scrollOffset", "altText", "style"]))
      : _createCommentVNode("", true),
    (state.prefersReducedMotion)
      ? (_openBlock(), _createBlock(ResponsiveBackground, {
          key: 1,
          id: `${props.sectionData.section}-illo`,
          scrollOffset: _unref(responsiveScrollOffset),
          altText: _unref(text).illoAltText,
          position: "50% 50%",
          dir: "mjp",
          sm: "conclusion.png",
          md: "conclusion.png",
          lg: "conclusion.png",
          style: _normalizeStyle({
        opacity: opacityForPageRange(2, 99),
      })
        }, null, 8, ["id", "scrollOffset", "altText", "style"]))
      : _createCommentVNode("", true),
    _createVNode(BottomGradient),
    _createVNode(SectionBase, {
      ref_key: "sectionBase",
      ref: sectionBase,
      thisSection: props.sectionData.section,
      currentSection: props.currentSection,
      currentSectionHasLoaded: props.currentSectionHasLoaded,
      currentPage: props.currentPage,
      forward: props.forward,
      position: props.sectionData.focalPoint
    }, {
      default: _withCtx(() => [
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-1`,
          class: "flex items-center justify-center"
        }, {
          default: _withCtx(() => [
            _createVNode(TextCard, {
              text: _unref(text).cleanupEfforts,
              enclosed: true,
              size: "sm",
              maxWidth: "650px"
            }, null, 8, ["text"])
          ]),
          _: 1
        }, 8, ["id"]),
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-2`,
          class: "flex items-center justify-center"
        }, {
          default: _withCtx(() => [
            _createVNode(TextCard, {
              text: _unref(text).healthierPlace,
              enclosed: true,
              size: "sm",
              maxWidth: "650px"
            }, null, 8, ["text"])
          ]),
          _: 1
        }, 8, ["id"]),
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-3`,
          class: "flex items-center justify-center"
        }, {
          default: _withCtx(() => [
            _createVNode(TextCard, {
              text: _unref(text).bringingItAlive,
              enclosed: true,
              size: "sm",
              maxWidth: "650px"
            }, null, 8, ["text"])
          ]),
          _: 1
        }, 8, ["id"]),
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-4`,
          class: "flex items-center justify-center"
        }, {
          default: _withCtx(() => [
            _createVNode(TextCard, {
              text: _unref(text).tendingInfrastructure,
              enclosed: true,
              size: "sm",
              maxWidth: "650px"
            }, null, 8, ["text"])
          ]),
          _: 1
        }, 8, ["id"])
      ]),
      _: 1
    }, 8, ["thisSection", "currentSection", "currentSectionHasLoaded", "currentPage", "forward", "position"])
  ]))
}
}

})